'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type OnboardingStep = 'role-intro' | 'framework-walkthrough' | 'quest-preview' | 'amanah-commitment';

interface RoleProfile {
  id: string;
  label: string;
  arabic: string;
  tagline: string;
  desc: string;
  color: string;
  icon: string;
  zahir: string;
  batin: string;
  firstQuest: string;
  questHref: string;
  suggestedStart: string;
  startHref: string;
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const ROLES: RoleProfile[] = [
  {
    id: 'inner-seeker',
    label: 'Inner Seeker',
    arabic: 'طَالِب الْبَاطِن',
    tagline: 'The one who turns inward to find the outward',
    desc: 'You are drawn to the interior dimensions of existence — the Batin beneath the Zahir. Your journey begins with recognition of the self as a sign.',
    color: '#D4A82A',
    icon: '◎',
    zahir: 'Observation of signs in creation',
    batin: 'Recognition of the Names within',
    firstQuest: 'Walk the IRL Lens — All 8 Layers',
    questHref: '/inner-reality-lens',
    suggestedStart: 'Keys to Kulli Haal',
    startHref: '/keys-to-kulli-haal',
  },
  {
    id: 'community-caretaker',
    label: 'Community Caretaker',
    arabic: 'خَادِم الْأُمَّة',
    tagline: 'The one who holds the community as Amanah',
    desc: 'You carry the weight of collective wellbeing. Your framework is the community itself — its gaps, its gifts, its Haal.',
    color: '#2d7a47',
    icon: '⬡',
    zahir: 'Mapping community resources and gaps',
    batin: 'Understanding collective Haal',
    firstQuest: 'Map Your City\'s Gaps in the MRG',
    questHref: '/muslim-resource-guide',
    suggestedStart: 'Muslim Resource Guide',
    startHref: '/muslim-resource-guide',
  },
  {
    id: 'academic',
    label: 'Academic',
    arabic: 'طَالِب الْعِلْم',
    tagline: 'The one who seeks the root of every branch',
    desc: 'You approach the framework as methodology — epistemological, rigorous, cross-referenced. Your Amanah is to make the knowledge transmissible.',
    color: '#8a8ab8',
    icon: '✦',
    zahir: 'Etymology, cross-reference, annotation',
    batin: 'The ontological structure of the Names',
    firstQuest: 'Map the Names Ontology — 10 Names',
    questHref: '/names-reference',
    suggestedStart: 'Names Reference',
    startHref: '/names-reference',
  },
  {
    id: 'activist',
    label: 'Activist',
    arabic: 'مُصْلِح',
    tagline: 'The one who sees Fasad and moves toward Islah',
    desc: 'You are oriented toward justice — environmental, social, systemic. The framework gives your work a sacred vocabulary and a deeper root.',
    color: '#2d8a87',
    icon: '⌘',
    zahir: 'Environmental and social justice mapping',
    batin: 'Mizan as the ground of all reform',
    firstQuest: 'Map Environmental Justice Through the IRL Lens',
    questHref: '/inner-reality-lens',
    suggestedStart: 'Inner Reality Lens',
    startHref: '/inner-reality-lens',
  },
  {
    id: 'explorer',
    label: 'Explorer',
    arabic: 'مُسَافِر',
    tagline: 'The one who arrives without a map',
    desc: 'You are new here. You do not yet know which role fits — and that is exactly right. Begin with curiosity. The framework will reveal itself.',
    color: '#c9a84c',
    icon: '◈',
    zahir: 'Exploring the framework without a fixed path',
    batin: 'Discovering your Haal through encounter',
    firstQuest: 'Begin the Guided Walkthrough',
    questHref: '/guided-walkthrough',
    suggestedStart: 'Guided Walkthrough',
    startHref: '/guided-walkthrough',
  },
  {
    id: 'local-resource',
    label: 'Local Resource',
    arabic: 'مَوْرِد مَحَلِّي',
    tagline: 'The one who makes themselves findable',
    desc: 'You are a provider — a masjid, clinic, school, or service. Your Amanah is to be present and accessible to those who need you.',
    color: '#5a5a8a',
    icon: '⬡',
    zahir: 'Listing and connecting community services',
    batin: 'Understanding your service as Amanah',
    firstQuest: 'List Your Resource in the MRG',
    questHref: '/muslim-resource-guide',
    suggestedStart: 'Muslim Resource Guide',
    startHref: '/muslim-resource-guide',
  },
];

const FRAMEWORK_STEPS = [
  {
    id: 'keys',
    label: 'KEYS TO KULLI HAAL',
    arabic: 'مَفَاتِيح',
    color: '#D4A82A',
    icon: '⌘',
    desc: 'Seven conceptual frameworks that form the vocabulary of the Haal Project. Ayat, Haal, Taqwah, Mizan, Amanah, Falah, Yaqeen.',
    href: '/keys-to-kulli-haal',
  },
  {
    id: 'ayat',
    label: 'AYAT IN CREATION',
    arabic: 'آيَات',
    color: '#2d7a47',
    icon: '☽',
    desc: 'Signs of Allah in creation — ecosystems, communities, patterns. The open-world journey of recognition.',
    href: '/ayat-in-creation',
  },
  {
    id: 'names',
    label: 'NAMES REFERENCE',
    arabic: 'أَسْمَاء',
    color: '#8a8ab8',
    icon: '✦',
    desc: '99 Names of Allah — the vocabulary of recognition. Each Name is a lens through which creation becomes legible.',
    href: '/names-reference',
  },
  {
    id: 'phases',
    label: 'FIVE HAALS',
    arabic: 'مَرَاحِل',
    color: '#2d8a87',
    icon: '◎',
    desc: 'The five stages of the caretaker\'s journey — from first encounter to Uboor (crossing). Each Haal is a station.',
    href: '/phase/haal-01',
  },
  {
    id: 'irl',
    label: 'INNER REALITY LENS',
    arabic: 'عَدَسَة',
    color: '#c9a84c',
    icon: '◈',
    desc: 'The application tool — eight layers from Mata (encounter) to Uboor (crossing). Recognition made practical.',
    href: '/inner-reality-lens',
  },
  {
    id: 'mrg',
    label: 'MUSLIM RESOURCE GUIDE',
    arabic: 'دَلِيل',
    color: '#2d7a47',
    icon: '⬡',
    desc: 'The community utility — connecting caretakers to resources, and resources to those who need them.',
    href: '/muslim-resource-guide',
  },
];

const AMANAH_COMMITMENTS = [
  'I will approach this framework with sincerity (Ikhlas) — not to perform knowledge, but to live it.',
  'I will hold my role as Amanah — a trust, not an identity.',
  'I will return to what I do not understand, rather than skip past it.',
  'I will connect what I learn here to what I observe in my locality.',
  'I will share what is beneficial and protect what is sacred.',
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const router = useRouter();
  const [step, setStep] = useState<OnboardingStep>('role-intro');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [frameworkStep, setFrameworkStep] = useState(0);
  const [checkedCommitments, setCheckedCommitments] = useState<Set<number>>(new Set());
  const [amanahSigned, setAmanahSigned] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const existingRole = localStorage.getItem('haal_role');
      if (existingRole) setSelectedRole(existingRole);
    }
  }, []);

  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_role', roleId);
    }
  };

  const handleCommitmentToggle = (idx: number) => {
    setCheckedCommitments(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleEnterDashboard = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_onboarding_complete', 'true');
      localStorage.setItem('haal_role', selectedRole || 'explorer');
    }
    router.push('/user-dashboard');
  };

  const allCommitmentsChecked = checkedCommitments.size === AMANAH_COMMITMENTS.length;
  const selectedRoleProfile = ROLES.find(r => r.id === selectedRole);

  const STEPS: { id: OnboardingStep; label: string }[] = [
    { id: 'role-intro', label: 'Your Role' },
    { id: 'framework-walkthrough', label: 'The Framework' },
    { id: 'quest-preview', label: 'First Quest' },
    { id: 'amanah-commitment', label: 'Amanah' },
  ];

  const currentStepIdx = STEPS.findIndex(s => s.id === step);

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <main className="max-w-[900px] mx-auto px-4 md:px-6 pt-24 pb-20">

        {/* Progress bar */}
        <div className="mb-10">
          <div className="flex items-center gap-0 mb-3">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.id}>
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => idx <= currentStepIdx && setStep(s.id)}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center font-mono text-[9px] font-black transition-all duration-300"
                    style={{
                      background: idx < currentStepIdx ? '#B38A2A' : idx === currentStepIdx ? 'rgba(179,138,42,0.15)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${idx <= currentStepIdx ? 'rgba(179,138,42,0.6)' : 'rgba(255,255,255,0.08)'}`,
                      color: idx <= currentStepIdx ? '#D4A82A' : 'rgba(245,240,232,0.2)',
                    }}
                  >
                    {idx < currentStepIdx ? '✓' : idx + 1}
                  </div>
                  <span
                    className="font-mono text-[9px] uppercase tracking-widest hidden sm:block"
                    style={{ color: idx === currentStepIdx ? 'rgba(179,138,42,0.8)' : 'rgba(245,240,232,0.2)' }}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 h-px" style={{ background: idx < currentStepIdx ? 'rgba(179,138,42,0.4)' : 'rgba(255,255,255,0.06)' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── STEP 1: Role Intro ── */}
        {step === 'role-intro' && (
          <div>
            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>◈ Step 1 of 4</p>
              <h1 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                Who Are You in This Work?
              </h1>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, lineHeight: 1.8, maxWidth: 600 }}>
                The Haal Project is not a single path — it is a framework that adapts to your role. Select the profile that most closely describes your current Haal. You can change this at any time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {ROLES.map(role => (
                <button
                  key={role.id}
                  onClick={() => handleRoleSelect(role.id)}
                  className="text-left p-5 rounded-xl transition-all duration-300"
                  style={{
                    background: selectedRole === role.id ? `${role.color}10` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedRole === role.id ? role.color + '50' : 'rgba(255,255,255,0.06)'}`,
                    borderLeft: `3px solid ${selectedRole === role.id ? role.color : 'transparent'}`,
                  }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-xl flex-shrink-0 mt-0.5" style={{ color: role.color }}>{role.icon}</span>
                    <div>
                      <h3 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{role.label}</h3>
                      <p className="font-mono text-[9px] mt-0.5" style={{ color: `${role.color}80`, direction: 'rtl', textAlign: 'left' }}>{role.arabic}</p>
                    </div>
                    {selectedRole === role.id && (
                      <span className="ml-auto font-mono text-[9px] px-2 py-0.5 rounded" style={{ background: `${role.color}20`, color: role.color, border: `1px solid ${role.color}30` }}>Selected</span>
                    )}
                  </div>
                  <p className="font-mono text-[10px] italic mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>{role.tagline}</p>
                  <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.6 }}>{role.desc}</p>
                  {selectedRole === role.id && (
                    <div className="mt-3 pt-3 grid grid-cols-2 gap-2" style={{ borderTop: `1px solid ${role.color}20` }}>
                      <div>
                        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: `${role.color}60` }}>Zahir</p>
                        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>{role.zahir}</p>
                      </div>
                      <div>
                        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: `${role.color}60` }}>Batin</p>
                        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>{role.batin}</p>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => selectedRole && setStep('framework-walkthrough')}
                disabled={!selectedRole}
                className="font-mono text-[11px] tracking-widest uppercase px-8 py-3 transition-all duration-200"
                style={{
                  background: selectedRole ? 'rgba(179,138,42,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${selectedRole ? 'rgba(179,138,42,0.5)' : 'rgba(255,255,255,0.08)'}`,
                  color: selectedRole ? '#D4A82A' : 'rgba(245,240,232,0.2)',
                  cursor: selectedRole ? 'pointer' : 'not-allowed',
                }}
              >
                Continue → The Framework
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Framework Walkthrough ── */}
        {step === 'framework-walkthrough' && (
          <div>
            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>◈ Step 2 of 4</p>
              <h1 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                The Framework
              </h1>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, lineHeight: 1.8, maxWidth: 600 }}>
                Six interconnected modules form the Haal Project framework. Each is a door — you do not need to enter all of them at once. Begin where your Haal calls you.
              </p>
            </div>

            {/* Framework node selector */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {FRAMEWORK_STEPS.map((fs, idx) => (
                <button
                  key={fs.id}
                  onClick={() => setFrameworkStep(idx)}
                  className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 transition-all duration-200"
                  style={{
                    background: frameworkStep === idx ? `${fs.color}15` : 'transparent',
                    border: `1px solid ${frameworkStep === idx ? fs.color + '50' : 'rgba(255,255,255,0.08)'}`,
                    color: frameworkStep === idx ? fs.color : 'rgba(245,240,232,0.3)',
                  }}
                >
                  {fs.icon} {fs.label}
                </button>
              ))}
            </div>

            {/* Active framework module */}
            {(() => {
              const fs = FRAMEWORK_STEPS[frameworkStep];
              return (
                <div className="rounded-xl p-8 mb-8" style={{ background: `${fs.color}06`, border: `1px solid ${fs.color}20`, borderLeft: `3px solid ${fs.color}` }}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-3xl" style={{ color: fs.color }}>{fs.icon}</span>
                    <div>
                      <h2 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{fs.label}</h2>
                      <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${fs.color}80`, direction: 'rtl' }}>{fs.arabic}</p>
                    </div>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.8, marginBottom: 16 }}>{fs.desc}</p>
                  <Link
                    href={fs.href}
                    className="inline-block font-mono text-[10px] tracking-widest uppercase px-5 py-2.5 transition-colors"
                    style={{ background: `${fs.color}20`, color: fs.color, border: `1px solid ${fs.color}40` }}
                  >
                    Preview {fs.label} →
                  </Link>
                </div>
              );
            })()}

            {/* Role-specific note */}
            {selectedRoleProfile && (
              <div className="rounded-lg p-4 mb-8" style={{ background: `${selectedRoleProfile.color}06`, border: `1px solid ${selectedRoleProfile.color}20` }}>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${selectedRoleProfile.color}70` }}>
                  For {selectedRoleProfile.label}s
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6 }}>
                  Your suggested entry point is <strong style={{ color: selectedRoleProfile.color }}>{selectedRoleProfile.suggestedStart}</strong>. This is where your role's Zahir dimension is most directly engaged.
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep('role-intro')}
                className="font-mono text-[11px] tracking-widest uppercase px-6 py-3 transition-all duration-200"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(245,240,232,0.3)' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep('quest-preview')}
                className="font-mono text-[11px] tracking-widest uppercase px-8 py-3 transition-all duration-200"
                style={{ background: 'rgba(179,138,42,0.15)', border: '1px solid rgba(179,138,42,0.5)', color: '#D4A82A' }}
              >
                Continue → First Quest
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Quest Preview ── */}
        {step === 'quest-preview' && (
          <div>
            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>◈ Step 3 of 4</p>
              <h1 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                Your First Quest
              </h1>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, lineHeight: 1.8, maxWidth: 600 }}>
                Quests are not tasks — they are invitations. Each one is calibrated to your role and your current Haal. Here is where your journey begins.
              </p>
            </div>

            {selectedRoleProfile && (
              <div className="rounded-xl p-8 mb-6" style={{ background: `${selectedRoleProfile.color}08`, border: `1px solid ${selectedRoleProfile.color}30`, borderLeft: `4px solid ${selectedRoleProfile.color}` }}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded" style={{ background: `${selectedRoleProfile.color}15`, color: selectedRoleProfile.color, border: `1px solid ${selectedRoleProfile.color}30` }}>
                    Entry Quest · {selectedRoleProfile.label}
                  </span>
                </div>
                <h2 className="font-mono text-xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
                  {selectedRoleProfile.firstQuest}
                </h2>
                <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
                  This quest is designed for your role as a <strong style={{ color: selectedRoleProfile.color }}>{selectedRoleProfile.label}</strong>. It engages your Zahir dimension first — the observable, the practical, the immediate.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Difficulty</p>
                    <p className="font-mono text-xs font-black" style={{ color: selectedRoleProfile.color }}>Entry Level</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Reward</p>
                    <p className="font-mono text-xs font-black" style={{ color: selectedRoleProfile.color }}>First Step Badge</p>
                  </div>
                </div>
                <Link
                  href={selectedRoleProfile.questHref}
                  className="inline-block font-mono text-[11px] tracking-widest uppercase px-6 py-3 transition-colors"
                  style={{ background: `${selectedRoleProfile.color}20`, color: selectedRoleProfile.color, border: `1px solid ${selectedRoleProfile.color}40` }}
                >
                  Begin Quest →
                </Link>
              </div>
            )}

            {/* Quest system preview */}
            <div className="rounded-lg p-5 mb-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Quest System Overview</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Entry', desc: 'First encounters', color: '#2d7a47', icon: '◎' },
                  { label: 'Intermediate', desc: 'Deeper engagement', color: '#D4A82A', icon: '✦' },
                  { label: 'Deep', desc: 'Scholarly depth', color: '#8a8ab8', icon: '⌘' },
                ].map(d => (
                  <div key={d.label} className="rounded p-3 text-center" style={{ background: `${d.color}08`, border: `1px solid ${d.color}20` }}>
                    <span className="text-lg block mb-1" style={{ color: d.color }}>{d.icon}</span>
                    <p className="font-mono text-[9px] uppercase tracking-widest font-black" style={{ color: d.color }}>{d.label}</p>
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, marginTop: 2 }}>{d.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep('framework-walkthrough')}
                className="font-mono text-[11px] tracking-widest uppercase px-6 py-3 transition-all duration-200"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(245,240,232,0.3)' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep('amanah-commitment')}
                className="font-mono text-[11px] tracking-widest uppercase px-8 py-3 transition-all duration-200"
                style={{ background: 'rgba(179,138,42,0.15)', border: '1px solid rgba(179,138,42,0.5)', color: '#D4A82A' }}
              >
                Continue → Amanah
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Amanah Commitment ── */}
        {step === 'amanah-commitment' && (
          <div>
            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>◈ Step 4 of 4</p>
              <h1 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                The Amanah
              </h1>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, lineHeight: 1.8, maxWidth: 600 }}>
                Amanah is a trust — something held on behalf of another. Before entering the dashboard, acknowledge the commitments that ground this work. These are not rules. They are orientations.
              </p>
            </div>

            {/* Arabic verse */}
            <div className="rounded-xl p-6 mb-8 text-center" style={{ background: 'rgba(139,105,20,0.06)', border: '1px solid rgba(139,105,20,0.2)' }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: 'rgba(212,197,169,0.8)', direction: 'rtl', lineHeight: 1.8, marginBottom: 8 }}>
                إِنَّا عَرَضْنَا الْأَمَانَةَ عَلَى السَّمَاوَاتِ وَالْأَرْضِ وَالْجِبَالِ
              </p>
              <p className="font-mono text-[10px] tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>
                "Indeed, We offered the Trust to the heavens and the earth and the mountains..." — Al-Ahzab 33:72
              </p>
            </div>

            {/* Commitments */}
            <div className="space-y-3 mb-8">
              {AMANAH_COMMITMENTS.map((commitment, idx) => (
                <button
                  key={idx}
                  onClick={() => handleCommitmentToggle(idx)}
                  className="w-full text-left flex items-start gap-4 p-4 rounded-lg transition-all duration-200"
                  style={{
                    background: checkedCommitments.has(idx) ? 'rgba(139,105,20,0.08)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${checkedCommitments.has(idx) ? 'rgba(179,138,42,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div
                    className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded transition-all duration-200 mt-0.5"
                    style={{
                      background: checkedCommitments.has(idx) ? '#B38A2A' : 'transparent',
                      border: `1px solid ${checkedCommitments.has(idx) ? '#B38A2A' : 'rgba(255,255,255,0.15)'}`,
                    }}
                  >
                    {checkedCommitments.has(idx) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="#060d12" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <p style={{ color: checkedCommitments.has(idx) ? 'rgba(212,197,169,0.8)' : 'rgba(212,197,169,0.45)', fontSize: 13, lineHeight: 1.7 }}>
                    {commitment}
                  </p>
                </button>
              ))}
            </div>

            {/* Completion state */}
            {allCommitmentsChecked && !amanahSigned && (
              <div className="rounded-xl p-6 mb-6 text-center" style={{ background: 'rgba(139,105,20,0.08)', border: '1px solid rgba(179,138,42,0.3)' }}>
                <p className="font-mono text-sm font-black uppercase tracking-widest mb-2" style={{ color: '#D4A82A' }}>
                  Amanah Acknowledged
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13 }}>
                  You have accepted the trust. Your journey begins now.
                </p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <button
                onClick={() => setStep('quest-preview')}
                className="font-mono text-[11px] tracking-widest uppercase px-6 py-3 transition-all duration-200"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(245,240,232,0.3)' }}
              >
                ← Back
              </button>
              <div className="flex items-center gap-3">
                {!allCommitmentsChecked && (
                  <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>
                    {AMANAH_COMMITMENTS.length - checkedCommitments.size} remaining
                  </p>
                )}
                <button
                  onClick={handleEnterDashboard}
                  disabled={!allCommitmentsChecked}
                  className="font-mono text-[11px] tracking-widest uppercase px-8 py-3 transition-all duration-200"
                  style={{
                    background: allCommitmentsChecked ? 'rgba(179,138,42,0.2)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${allCommitmentsChecked ? 'rgba(179,138,42,0.6)' : 'rgba(255,255,255,0.06)'}`,
                    color: allCommitmentsChecked ? '#D4A82A' : 'rgba(245,240,232,0.15)',
                    cursor: allCommitmentsChecked ? 'pointer' : 'not-allowed',
                  }}
                >
                  Enter Dashboard →
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
      <Footer />
    </div>
  );
}
